import { graphql } from 'react-relay';

import { isGraphQLError } from '@confluence/network';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useBillingDetailsBillingDetailsQuery as useBillingDetailsBillingDetailsQuery$data,
	useBillingDetailsBillingDetailsQueryVariables as useBillingDetailsBillingDetailsQuery$variables,
} from './__types__/useBillingDetailsBillingDetailsQuery.ts';
type UseBillingDetailsProps = {
	cloudId: string;
	skip?: boolean;
};

type UseBillingDetailsResult = {
	loading: boolean;
	hasPaymentDetails: boolean;
	entitlementId?: string;
	transactionAccountId?: string | null;
};

export const useBillingDetails = ({
	cloudId,
	skip = false,
}: UseBillingDetailsProps): UseBillingDetailsResult => {
	const { data, error, loading } = useQueryInterop<
		useBillingDetailsBillingDetailsQuery$data,
		useBillingDetailsBillingDetailsQuery$variables
	>(
		graphql`
			query useBillingDetailsBillingDetailsQuery($cloudId: ID!, $hamsProductKey: String!) {
				tenantContexts(cloudIds: [$cloudId]) {
					entitlementInfo(hamsProductKey: $hamsProductKey) {
						entitlementId
						entitlement {
							... on CcpEntitlement {
								transactionAccountId
								subscription {
									accountDetails {
										invoiceGroup {
											invoiceable
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			variables: { cloudId, hamsProductKey: 'confluence.ondemand' },
			skip,
			client: getAGGClient(),
			onError: (error) => {
				if (isUnauthorizedError(error) || isGraphQLError(error)) markErrorAsHandled(error);
			},
		},
	);

	const result: UseBillingDetailsResult = {
		loading,
		hasPaymentDetails: false,
	};

	if (!loading && error) {
		return result;
	}

	const entitlementInfo = data?.tenantContexts?.[0]?.entitlementInfo;

	if (entitlementInfo) {
		const { entitlement, entitlementId } = entitlementInfo;

		result.entitlementId = entitlementId;

		if (entitlement?.__typename === 'CcpEntitlement') {
			result.hasPaymentDetails =
				entitlement.subscription?.accountDetails?.invoiceGroup?.invoiceable ?? false;

			result.transactionAccountId = entitlement.transactionAccountId;
		}
	}

	return result;
};
